import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Typography,
  Select,
  MenuItem,
  Box,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm"; // Optional: Enables GitHub Flavored Markdown

// Import markdown file contents for About, Privacy, and Terms.
import termsOfServiceContent from "./terms_of_service.md";
import privacyPolicyContent from "./privacy_policy.md";
import aboutContent from "./about.md";
import hipaaSecurityContent from "./hipaa_security.md";
import smsOptInContent from "./sms_opt_in.md";

// Import your FAQ with guide component – ensure this is a React component, not a markdown file.
import FaqWithGuide from "./faqWithGuide";

const TAB_CONFIG = [
  { path: "/about", label: "About", markdown: aboutContent },
  { path: "/privacy", label: "Privacy Policy", markdown: privacyPolicyContent },
  { path: "/terms", label: "Terms & Conditions", markdown: termsOfServiceContent },
  { path: "/sms-opt-in", label: "SMS Opt-In Policy", markdown: smsOptInContent },
  { path: "/hipaa-security", label: "HIPAA & Security", markdown: hipaaSecurityContent },
  { path: "/faq", label: "FAQ", markdown: null, isFaq: true },
];

function getBrandNotice() {
  const host =
    typeof window !== "undefined"
      ? String(window.location.hostname || "").toLowerCase()
      : "";
  if (host.includes("timely365")) {
    return "Timely365 is a product operated by Micro Logic LLC. Micro Logic LLC also operates Plany365 and AgentCrafty. AgentCrafty provides AI phone agent, SMS, chat, booking, and customer communication workflows that may support Timely365 or related business workflows.";
  }
  return "Plany365 is a product operated by Micro Logic LLC. Micro Logic LLC also operates Timely365 and AgentCrafty. AgentCrafty provides AI phone agent, SMS, chat, booking, and customer communication workflows that may support Plany365 or related business workflows.";
}

const sharedOwnershipNotice =
  "Micro Logic LLC operates Plany365, Timely365, and AgentCrafty. Plany365, Timely365, and AgentCrafty are products and services of Micro Logic LLC. References to “Plany365,” “Timely365,” “AgentCrafty,” “Micro Logic,” “we,” “our,” and “us” refer to Micro Logic LLC and its related products and services.";

const PrivacyTermsAbout = () => {
  const location = useLocation();
  const [markdownContent, setMarkdownContent] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTab, setSelectedTab] = useState(0);

  // When the location changes, update the tab and fetch content if needed.
  useEffect(() => {
    const pathKeys = TAB_CONFIG.map((tab) => tab.path);
    const tabIndex = pathKeys.indexOf(location.pathname);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0); // Default to "About" if path doesn't match
    fetchMarkdownFile(location.pathname);
  }, [location.pathname]);

  // Fetch the markdown file only if the current path is not "/faq"
  const fetchMarkdownFile = (filePath) => {
    const tab = TAB_CONFIG.find((item) => item.path === filePath);
    if (!tab || tab.isFaq) {
      setMarkdownContent("");
      return;
    }
    const markdownFilePath = tab.markdown;
    if (markdownFilePath) {
      fetch(markdownFilePath)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch the markdown file");
          }
          return response.text();
        })
        .then((text) => setMarkdownContent(text))
        .catch((error) =>
          console.error("Error fetching markdown file:", error)
        );
    } else {
      setMarkdownContent("Content not found.");
    }
  };

  // Update content when the selected tab changes (if not FAQ)
  useEffect(() => {
    const path = TAB_CONFIG[selectedTab]?.path;
    if (path && path !== "/faq") {
      fetchMarkdownFile(path);
    }
  }, [selectedTab]);

  const getTabBackgroundColor = (index) =>
    selectedTab === index ? "rgba(0, 0, 0, 0.1)" : "transparent";

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        {!isSmallScreen ? (
          <Grid item xs={3}>
            <Paper>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedTab}
                onChange={(event, newValue) => setSelectedTab(newValue)}
              >
                {TAB_CONFIG.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    sx={{
                      backgroundColor: getTabBackgroundColor(index),
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ padding: 3, marginBottom: 3 }}>
              <Select
                fullWidth
                value={selectedTab}
                onChange={(event) => setSelectedTab(event.target.value)}
              >
                <MenuItem value={0}>About</MenuItem>
                <MenuItem value={1}>Privacy Policy</MenuItem>
                <MenuItem value={2}>Terms & Conditions</MenuItem>
                <MenuItem value={3}>SMS Opt-In Policy</MenuItem>
                <MenuItem value={4}>HIPAA & Security</MenuItem>
                <MenuItem value={5}>FAQ</MenuItem>
              </Select>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={isSmallScreen ? 12 : 9}>
          <Paper sx={{ padding: 3 }}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                Micro Logic LLC product notice
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.7 }}>
                {sharedOwnershipNotice}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {getBrandNotice()}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 1.5 }}>
                <Link href="/privacy">Privacy Policy</Link>
                {" | "}
                <Link href="/terms">Terms & Conditions</Link>
                {" | "}
                <Link href="/sms-opt-in">SMS Opt-In Policy</Link>
              </Typography>
            </Box>
            <Typography variant="body1" align="justify" sx={{ margin: 3 }}>
              {TAB_CONFIG[selectedTab]?.isFaq ? (
                // Render FAQ component for FAQ tab
                <FaqWithGuide />
              ) : (
                markdownContent && (
                  <ReactMarkdown
                    remarkPlugins={[gfm]}
                    children={markdownContent}
                  />
                )
              )}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PrivacyTermsAbout;
